import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Loader2, ArrowLeft, ArrowRight, DollarSign, Shield, Heart, Clock, 
  Calendar, PiggyBank, Gift, BarChart3, Target, Info, CheckCircle,
  Briefcase, Star, TrendingUp, AlertTriangle
} from "lucide-react";
import { ReportMarkdownRenderer } from "@/components/ReportMarkdownRenderer";

const PROGRESS_STAGES = [
  "Scraping job posting data...",
  "Searching Glassdoor salary reports...",
  "Cross-referencing Indeed & Payscale...",
  "Checking Levels.fyi & BLS data...",
  "Researching H1B salary filings...",
  "Identifying health insurance provider...",
  "Analyzing benefits packages...",
  "Calculating total compensation value...",
  "Comparing against market averages...",
  "Building negotiation intelligence...",
  "Compiling final report..."
];

export default function SalaryIntelReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [jobText, setJobText] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [location, setLocation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [progressStage, setProgressStage] = useState(0);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!user) navigate("/auth");
  }, [user, navigate]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPolling) {
      interval = setInterval(() => {
        setProgressStage(prev => (prev + 1) % PROGRESS_STAGES.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isPolling]);

  const pollForReport = async (startTime: Date) => {
    const pollInterval = setInterval(async () => {
      const { data } = await supabase
        .from('salary_intel_reports' as any)
        .select('*')
        .eq('user_id', user?.id)
        .gte('created_at', startTime.toISOString())
        .in('status', ['completed', 'failed'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        clearInterval(pollInterval);
        setIsPolling(false);
        setReport(data);
        if ((data as any).status === 'failed') {
          toast({ title: "Report Failed", description: (data as any).message || (data as any).error || "Unable to generate report", variant: "destructive" });
        } else {
          toast({ title: "Report Complete", description: "Your Salary & Benefits Intelligence report is ready!" });
        }
      }
    }, 5000);

    setTimeout(() => {
      clearInterval(pollInterval);
      if (isPolling) {
        setIsPolling(false);
        toast({ title: "Timeout", description: "Report generation is taking longer than expected. Check your reports library later.", variant: "destructive" });
      }
    }, 600000);
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      toast({ title: "Error", description: "You must be logged in", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    setProgressStage(0);
    try {
      const payload: Record<string, string> = { user_id: user.id };
      if (jobText.trim()) payload.job_posting_text = jobText.trim();
      if (jobUrl.trim()) payload.job_url = jobUrl.trim();
      if (companyName.trim()) payload.company_name = companyName.trim();
      if (jobTitle.trim()) payload.job_title = jobTitle.trim();
      if (location.trim()) payload.location = location.trim();

      const { error } = await supabase.functions.invoke('trigger-salary-intel', { body: payload });
      if (error) throw error;

      toast({ title: "Report Requested", description: "Generating your Salary & Benefits report. This typically takes 3-8 minutes." });
      setIsPolling(true);
      pollForReport(new Date());
    } catch (err: any) {
      console.error('Error triggering report:', err);
      toast({ title: "Error", description: err.message || "Failed to request report", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Report display
  if (report && (report as any).status === 'completed') {
    return (
      <div className="container mx-auto py-8 px-4 max-w-4xl">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/salary-intel-reports')}>
            View All Reports
          </Button>
        </div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Salary & Benefits Intelligence Report</h1>
          <p className="text-muted-foreground">{(report as any).company_name} — {(report as any).job_title}</p>
        </div>

        {(report as any).report ? (
          <Card>
            <CardContent className="p-6">
              <ReportMarkdownRenderer content={(report as any).report} />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6 text-muted-foreground">No report content available.</CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Polling state
  if (isPolling) {
    return (
      <div className="min-h-screen bg-green-50/50 flex items-center justify-center">
        <Card className="max-w-lg w-full mx-4 border-2 border-green-200 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Researching Compensation Data</h2>
            <p className="text-muted-foreground mb-6">Cross-referencing 6+ salary databases and benefits sources...</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                <span className="text-sm font-medium text-green-800">{PROGRESS_STAGES[progressStage]}</span>
              </div>
            </div>
            <div className="flex justify-center gap-1">
              {PROGRESS_STAGES.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i <= progressStage ? 'bg-green-500' : 'bg-green-200'}`} />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">Typical processing time: 3–8 minutes</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Landing / form page
  return (
    <div className="min-h-screen bg-green-50/50">
      {/* Header */}
      <div className="border-b border-green-200 bg-white/80 backdrop-blur-sm px-6 py-4">
        <div className="container mx-auto max-w-5xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/jobintel-360-reports')} className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Salary & Benefits Intelligence</h1>
              <p className="text-sm text-muted-foreground">Know the real comp package before you negotiate</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl py-8 px-4">
        {/* Hero Card */}
        <Card className="border-2 border-green-300 shadow-xl mb-8 bg-white">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Know What the Job <span className="text-green-600">Actually</span> Pays
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Even when the posting says "competitive salary" — we uncover exact ranges, insurance providers by name, co-pays, PTO days, 401k match details, and your total compensation value across 6+ data sources.
              </p>
            </div>

            {/* What you'll discover link */}
            <div className="flex justify-center mb-6">
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-green-600 hover:text-green-700 underline decoration-green-400 underline-offset-4 font-semibold transition-colors">
                    <Info className="w-4 h-4 mr-1.5" />
                    What you'll discover →
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-green-600">
                      <DollarSign className="w-5 h-5" />
                      What You'll Learn in Your Report
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[75vh] pr-4">
                    <div className="space-y-4">
                      {[
                        { icon: DollarSign, title: "Estimated Salary Range", desc: "Cross-referenced across Glassdoor, Indeed, Payscale, Levels.fyi, LinkedIn, BLS data, and H1B filings to find the real range even when unlisted" },
                        { icon: TrendingUp, title: "Commission & Variable Comp", desc: "OTE, base/variable splits, quota expectations, bonus structures, and equity details for sales and incentive-based roles" },
                        { icon: Shield, title: "Health Insurance Provider Names", desc: "Identifies the actual carrier (Blue Cross, Aetna, UnitedHealthcare, Cigna, Kaiser) through employee reviews, DOL filings, and company data" },
                        { icon: Heart, title: "Co-Pay & Deductible Details", desc: "Finds reported co-pay amounts, deductible ranges, and plan types (PPO/HMO/HDHP) from employee-sourced data" },
                        { icon: Clock, title: "Benefits Start Date", desc: "Determines whether coverage begins Day 1, 30 days, 60 days, or 90 days from hire" },
                        { icon: Calendar, title: "PTO & Vacation Days", desc: "Exact day counts, accrual policies, sick day structures, and the real story behind 'unlimited PTO'" },
                        { icon: PiggyBank, title: "401k & Retirement", desc: "Match percentages, vesting schedules, plan providers, plus HSA/FSA availability" },
                        { icon: Gift, title: "Full Benefits Inventory", desc: "Life insurance, disability, parental leave, tuition reimbursement, wellness programs, remote work stipends, and every perk on the table" },
                        { icon: BarChart3, title: "Market Comparison", desc: "Side-by-side comparison showing where this package sits vs. market average across every category" },
                        { icon: Target, title: "Negotiation Intelligence", desc: "What's negotiable, what's fixed, your strongest leverage points, and a recommended target salary backed by data" },
                        { icon: Star, title: "Every Claim Cited", desc: "Source name, date, confidence rating (⭐⭐⭐ Verified / ⭐⭐ Likely / ⭐ Estimated / ❓ Unverified) for every data point" },
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <item.icon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-foreground text-sm">{item.title}</h4>
                            <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {/* 4 Feature Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: DollarSign, title: "Salary Range", desc: "6+ data sources" },
                { icon: Shield, title: "Insurance Names", desc: "Actual carriers" },
                { icon: PiggyBank, title: "401k Match", desc: "Vesting details" },
                { icon: Target, title: "Negotiation Intel", desc: "Data-backed targets" },
              ].map((f, i) => (
                <div key={i} className="bg-white rounded-xl p-4 text-center border-2 border-green-600 shadow-sm">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <f.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="font-semibold text-sm text-foreground">{f.title}</div>
                  <div className="text-xs text-muted-foreground">{f.desc}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Input Form */}
        <Card className="border-2 border-green-200 shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Generate Your Report
            </CardTitle>
            <CardDescription>All fields are optional — provide whatever you have for the best results</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <Input placeholder="e.g. Google" value={companyName} onChange={e => setCompanyName(e.target.value)} />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input placeholder="e.g. Senior Product Manager" value={jobTitle} onChange={e => setJobTitle(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input placeholder="e.g. San Francisco, CA" value={location} onChange={e => setLocation(e.target.value)} />
              </div>
              <div>
                <Label>Job URL (optional)</Label>
                <Input placeholder="https://..." value={jobUrl} onChange={e => setJobUrl(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Job Posting Text (optional — paste for best results)</Label>
              <Textarea
                placeholder="Paste the full job description here..."
                value={jobText}
                onChange={e => setJobText(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              size="lg"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Triggering Report...</>
              ) : (
                <><DollarSign className="w-4 h-4 mr-2" /> Generate Salary & Benefits Report</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
